'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { BookOpen, Bug, Calculator, ChevronDown, DollarSign, FileText, Info, LifeBuoy, LogOut, MapPin, Menu, MessageSquarePlus, Moon, Shield, Sun, TrendingUp, User, X } from 'lucide-react'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList
} from '@/components/ui/navigation-menu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
const cn = (...classes: Array<string | undefined | false | null>) =>
  classes.filter(Boolean).join(' ')

type SubItem = { href: string; label: string; description: string; icon: typeof Calculator }
type NavLink = { href: string; label: string; children?: SubItem[] }

const coreLinks: NavLink[] = [
  { href: '/dashboard', label: 'Dashboard' },
  {
    href: '/tools',
    label: 'Tools',
    children: [
      { href: '/calculator', label: 'Pay Calculator', description: 'Model your total military compensation', icon: Calculator },
      { href: '/calculator/contract', label: 'Contract Modeler', description: 'Visualize the total value of your enlistment', icon: FileText },
      { href: '/calculator/transition', label: 'Transition Planner', description: 'Map your military-to-civilian finances', icon: TrendingUp },
      { href: '/calculator/bah', label: 'BAH Lookup', description: 'Compare housing allowances by location', icon: MapPin },
      { href: '/calculator/tsp', label: 'TSP Projector', description: 'Forecast your retirement savings growth', icon: DollarSign },
      { href: '/calculator/deployment', label: 'Deployment Savings', description: 'Calculate your combat deployment earnings', icon: Shield },
    ],
  },
  { href: '/markets', label: 'Markets' },
  { href: '/resources', label: 'Resources' }
]

type UserSummary = {
  name: string | null
  email: string | null
  avatarUrl: string | null
}

export default function Navbar() {
  const pathname = usePathname()
  const router = useRouter()
  const [user, setUser] = useState<UserSummary | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)

  useEffect(() => {
    const supabase = createBrowserSupabaseClient()

    const syncUser = async () => {
      const { data } = await supabase.auth.getSession()
      const sessionUser = data.session?.user ?? null
      if (!sessionUser) {
        setUser(null)
        return
      }
      setUser({
        name:
          sessionUser.user_metadata?.full_name ??
          sessionUser.user_metadata?.name ??
          null,
        email: sessionUser.email ?? null,
        avatarUrl:
          sessionUser.user_metadata?.avatar_url ??
          sessionUser.user_metadata?.picture ??
          null
      })
    }

    syncUser()

    const { data: listener } = supabase.auth.onAuthStateChange(() => {
      syncUser()
    })

    return () => {
      listener?.subscription?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const stored = localStorage.getItem('theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const nextIsDark = stored ? stored === 'dark' : prefersDark
    setIsDarkMode(nextIsDark)
    document.documentElement.classList.toggle('dark', nextIsDark)
  }, [])

  const displayName = useMemo(() => {
    if (user?.name) return user.name
    if (user?.email) return user.email.split('@')[0]
    return 'Account'
  }, [user])

  const displayEmail = user?.email ?? 'Not signed in'
  const initial = displayName ? displayName.charAt(0).toUpperCase() : 'A'

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(`${href}/`)
  }

  const handleSignOut = async () => {
    const supabase = createBrowserSupabaseClient()
    await supabase.auth.signOut()
    document.cookie = 'wm_access=; path=/; max-age=0; samesite=lax'
    router.push('/')
  }

  const handleMobileNav = (href: string) => {
    setIsMobileMenuOpen(false)
    router.push(href)
  }

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const next = !prev
      document.documentElement.classList.toggle('dark', next)
      localStorage.setItem('theme', next ? 'dark' : 'light')
      return next
    })
  }

  return (
    <nav className="fixed top-0 z-40 mx-auto w-full max-w-7xl border-b border-[var(--nav-border)] bg-[var(--nav-bg)] md:sticky md:z-50 md:backdrop-blur-md">
      <div className="flex h-16 items-center justify-between px-6 md:h-20 md:px-10">
        <Link href="/" className="flex items-center">
          <img
            src="/logo.png"
            alt="WarfighterMoney"
            className="h-20 w-auto dark:hidden"
          />
          <img
            src="/logo-dark.png"
            alt="WarfighterMoney"
            className="hidden h-20 w-auto dark:block"
          />
          <span className="sr-only">WarfighterMoney</span>
        </Link>

        <div className="hidden flex-1 items-center justify-center md:flex">
          <NavigationMenu>
            <NavigationMenuList className="rounded-full bg-[var(--nav-pill)] p-1">
              {coreLinks.map((link) =>
                link.children ? (
                  <NavigationMenuItem key={link.href} className="relative group">
                    <Link
                      href={link.href}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-all',
                        isActive(link.href)
                          ? 'bg-[var(--nav-pill-active)] text-[var(--nav-text-active)] shadow-sm ring-1 ring-[var(--border)]'
                          : 'text-[var(--nav-text)] hover:text-[var(--nav-text-active)]'
                      )}
                    >
                      {link.label}
                      <ChevronDown className="h-3 w-3 opacity-50 transition-transform group-hover:rotate-180" />
                    </Link>
                    {/* hover dropdown */}
                    <div className="pointer-events-none absolute left-0 top-full z-50 pt-2 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
                      <div className="w-84 rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-4 shadow-2xl" style={{ width: '21rem' }}>
                        {link.children.map((child, childIdx) => {
                          const Icon = child.icon
                          return (
                            <div key={child.href}>
                              {childIdx > 0 && (
                                <div className="mx-3 my-2 h-px bg-[var(--border)] opacity-40" />
                              )}
                              <Link
                                href={child.href}
                                className="group/item flex items-start gap-4 rounded-xl px-4 py-3.5 transition hover:bg-[var(--surface-2)]"
                              >
                                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500">
                                  <Icon className="h-[18px] w-[18px]" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-[var(--text-1)]">{child.label}</p>
                                  <p className="mt-0.5 grid grid-rows-[0fr] text-xs leading-relaxed text-[var(--text-3)] transition-[grid-template-rows,opacity] duration-200 opacity-0 group-hover/item:grid-rows-[1fr] group-hover/item:opacity-100">
                                    <span className="overflow-hidden">{child.description}</span>
                                  </p>
                                </div>
                              </Link>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </NavigationMenuItem>
                ) : (
                  <NavigationMenuItem key={link.href}>
                    <Link
                      href={link.href}
                      className={cn(
                        'rounded-full px-4 py-2 text-sm font-medium transition-all',
                        isActive(link.href)
                          ? 'bg-[var(--nav-pill-active)] text-[var(--nav-text-active)] shadow-sm ring-1 ring-[var(--border)]'
                          : 'text-[var(--nav-text)] hover:text-[var(--nav-text-active)]'
                      )}
                    >
                      {link.label}
                    </Link>
                  </NavigationMenuItem>
                )
              )}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <DropdownMenu>
            <DropdownMenuTrigger className="rounded-full border border-[var(--border)] bg-[var(--surface-1)]/90 p-1 shadow-sm transition hover:shadow-md">
              <Avatar className="h-9 w-9">
                {user?.avatarUrl ? (
                  <AvatarImage src={user.avatarUrl} alt={displayName} />
                ) : null}
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold text-[var(--text-1)]">{displayName}</div>
                <div className="text-xs text-[var(--text-3)]">{displayEmail}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => router.push('/profile')}>
                <User className="h-4 w-4 text-slate-500" />
                Profile & Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => router.push('/faq')}>
                <LifeBuoy className="h-4 w-4 text-slate-500" />
                Help & FAQ
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  window.location.href = 'mailto:info@warfightermoney.com?subject=Feature%20Request'
                }}
              >
                <MessageSquarePlus className="h-4 w-4 text-slate-500" />
                Request a Feature
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  window.location.href = 'mailto:info@warfightermoney.com?subject=Bug%20Report'
                }}
              >
                <Bug className="h-4 w-4 text-slate-500" />
                Report a Bug
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme}>
                {isDarkMode ? (
                  <Sun className="h-4 w-4 text-slate-500" />
                ) : (
                  <Moon className="h-4 w-4 text-slate-500" />
                )}
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => router.push('/about')}>
                <Info className="h-4 w-4 text-slate-500" />
                About Us
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                <LogOut className="h-4 w-4 text-red-500" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="md:hidden">
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(true)}
            className="inline-flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface-1)] text-[var(--text-2)] shadow-sm"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-64 transform bg-[var(--surface-1)] p-6 shadow-2xl transition-transform md:hidden">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold text-[var(--text-1)]">Menu</p>
                <p className="text-xs text-[var(--text-3)]">{displayEmail}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-full border border-[var(--border)] p-2 text-[var(--text-2)] hover:bg-[var(--surface-2)]"
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 flex flex-col gap-1">
              {coreLinks.map((link) =>
                link.children ? (
                  <div key={link.href}>
                    <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
                      {link.label}
                    </p>
                    {link.children.map((child) => {
                      const Icon = child.icon
                      return (
                        <button
                          key={child.href}
                          type="button"
                          onClick={() => handleMobileNav(child.href)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition',
                            isActive(child.href)
                              ? 'bg-[var(--surface-2)] font-semibold text-[var(--text-1)]'
                              : 'text-[var(--text-2)] hover:bg-[var(--surface-2)]'
                          )}
                        >
                          <Icon className="h-4 w-4 text-blue-500" />
                          {child.label}
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <button
                    key={link.href}
                    type="button"
                    onClick={() => handleMobileNav(link.href)}
                    className={cn(
                      'rounded-xl px-4 py-3 text-left text-sm font-semibold transition',
                      isActive(link.href)
                        ? 'bg-[var(--surface-2)] text-[var(--text-1)]'
                        : 'text-[var(--text-2)] hover:bg-[var(--surface-2)]'
                    )}
                  >
                    {link.label}
                  </button>
                )
              )}
            </div>

            <div className="my-6 h-px bg-[var(--border)]" />

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => handleMobileNav('/profile')}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              >
                <User className="h-4 w-4 text-slate-500" />
                Profile & Settings
              </button>
              <button
                type="button"
                onClick={() => handleMobileNav('/faq')}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              >
                <LifeBuoy className="h-4 w-4 text-slate-500" />
                Help & FAQ
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  window.location.href =
                    'mailto:info@warfightermoney.com?subject=Feature%20Request'
                }}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              >
                <MessageSquarePlus className="h-4 w-4 text-slate-500" />
                Request a Feature
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  window.location.href = 'mailto:info@warfightermoney.com?subject=Bug%20Report'
                }}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              >
                <Bug className="h-4 w-4 text-slate-500" />
                Report a Bug
              </button>
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              >
                {isDarkMode ? (
                  <Sun className="h-4 w-4 text-slate-500" />
                ) : (
                  <Moon className="h-4 w-4 text-slate-500" />
                )}
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                type="button"
                onClick={() => handleMobileNav('/about')}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              >
                <Info className="h-4 w-4 text-slate-500" />
                About Us
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                <LogOut className="h-4 w-4 text-red-500" />
                Log Out
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  )
}
